import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGhgOrganization } from 'src/common/hooks';
import { OrganizationTag } from 'src/utils/tags';
import { useRabenConsent } from 'src/Ghg/hooks';
import { DeclinePayload } from 'src/Ghg/interfaces';
import DeclineReasonForm from '../DeclineReasonForm';
import { SUPPLIER_COMMITMENT_TAB_PATH } from '../TargetPromptModal';

// C15 (pop-up 1 -- Supplier Commitment): specs/001-raben-country-
// masteradmin/mass-specs/specs/20260826-decl-two-popups-window.md, D-05
// resolved (reference/docs/README.md). Shown after login while the org has
// tag `raben`, the pilot flag + a per-branch display window are both true
// (server-computed -- DECISIONS-RESOLVED "C15 §2"), and no decision has
// been made yet (BR-2/BR-3).
//
// RG 28.08 1.2, D1: no X -- the pop-up goes away only through a decision
// (Esc and a click on the backdrop do nothing either). RG 28.08 1.4: Accept
// does not download the letter, it navigates to the Supplier Commitment tab,
// where the download button lives. RG 28.08 1.3, D3: "Odrzucam" does not
// post at once, it first shows the optional reason form. RG 28.08 D2: only
// the carrier's own users decide, so an admin browsing a carrier's account
// (`is_direct_member` false) never sees the pop-up.
export default function RabenConsentModal() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'ghg.rabenConsentModal',
  });
  const organization = useGhgOrganization();
  const navigate = useNavigate();
  const [step, setStep] = React.useState<'statement' | 'decline'>('statement');

  const {
    data: consentData,
    isLoading: consentLoading,
    accept,
    decline,
  } = useRabenConsent(organization.id);

  const isRaben = organization.tags.includes(OrganizationTag.Raben);

  // BR-2: no active window -> no pop-up, regardless of tag. BR-4/EC-5: this
  // gate is `consent === null` ("no decision"), which is mutually exclusive
  // with pop-up 2's `consent === true` ("accepted") gate in TargetPromptModal
  // -- the two can never both be eligible for the same org, so pop-up 1
  // "winning" falls out of the data rather than needing explicit precedence
  // code between the two (sibling) components.
  // `is_direct_member` is strict (`=== true`): every 403 from the POST
  // redirects to /403, so anyone who may not decide must not get the buttons.
  // RG live demo 2026-10-02: the branch's Letter window (set by the client in
  // Settings -> "Okna Listu Zobowiązania Przewoźnika") is the switch. The old
  // raben_commitment_pilot tag gate is gone -- the client has no way to set
  // that tag, so a window alone never showed anything.
  const show =
    isRaben &&
    !!consentData?.window_active &&
    !consentLoading &&
    consentData?.consent === null &&
    consentData?.is_direct_member === true;

  // Whenever the pop-up goes away (a decision was recorded), the next time
  // it appears it starts from the letter again, not from the reason form.
  React.useEffect(() => {
    if (!show) setStep('statement');
  }, [show]);

  const handleAccept = async () => {
    await accept.mutateAsync();
    navigate(SUPPLIER_COMMITMENT_TAB_PATH);
  };

  // The dialog disappears by itself once the refetched `consent` is false.
  const handleDecline = async (payload: DeclinePayload) => {
    await decline.mutateAsync(payload);
  };

  return (
    <Dialog open={!!show} onClose={() => {}} disableEscapeKeyDown>
      <DialogTitle>{t('title')}</DialogTitle>
      {step === 'statement' ? (
        <>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('statement')}
            </Typography>
            <Link
              href={t('letterUrl')}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('letterLinkLabel')}
            </Link>
          </DialogContent>
          <DialogActions>
            <Button
              variant="text"
              onClick={() => setStep('decline')}
              disabled={accept.isLoading}
            >
              {t('reject')}
            </Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              disabled={accept.isLoading}
            >
              {t('accept')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <DialogContent>
          <DeclineReasonForm
            context="letter"
            options={consentData?.decline_reason_options}
            loading={decline.isLoading}
            onConfirm={handleDecline}
            onCancel={() => setStep('statement')}
          />
        </DialogContent>
      )}
    </Dialog>
  );
}
